import { ArrowRight, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { EmptyState } from '@/ui/EmptyState';
import { GlossaryTerm } from '@/ui/GlossaryTerm';

import type { LoteProntoParaAprovacao } from '../contrato';
import { rotuloCurtoDaCompetencia } from '../rotulos';

import { SemRecortePorDre } from './SemRecortePorDre';

/**
 * "Lotes prontos para aprovação" — os que a SME pode decidir agora, os mais
 * antigos primeiro.
 *
 * O BOTÃO NÃO É "VER", E A LINHA NÃO É CLICÁVEL. A regra 9 de interface manda a
 * linha inteira abrir o detalhe em listagem com detalhe — mas não há tela de
 * detalhe de lote: os cinco atalhos levam todos para a mesma fila
 * (`/sme/apontamentos`), onde a decisão acontece. Linha clicável aqui prometeria
 * um detalhe por lote que ninguém construiu. Quando a tela de lote existir, a
 * linha vira o link e o botão sai.
 */

interface LotesProntosProps {
  lotes: LoteProntoParaAprovacao[];
  /**
   * `true` para quem não é SME: a fila junta lotes de várias diretorias e o
   * sistema ainda não sabe de qual diretoria é quem está olhando (ver
   * `SemRecortePorDre`).
   */
  semRecortePorDre: boolean;
}

export function LotesProntos({ lotes, semRecortePorDre }: LotesProntosProps) {
  const navegar = useNavigate();

  return (
    <Card className="rounded-2xl border border-border/60">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="h-4 w-4 text-primary" aria-hidden="true" />
          <span>
            <GlossaryTerm termo="LOTE_APONTAMENTO">Lotes</GlossaryTerm> prontos para aprovação
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {semRecortePorDre ? (
          <SemRecortePorDre />
        ) : lotes.length === 0 ? (
          <EmptyState
            title="Nenhum lote aguardando aprovação"
            description="Nenhuma DRE enviou lote para decisão da SME nesta competência."
          />
        ) : (
          lotes.map((lote) => (
            <div
              key={lote.loteId}
              className="flex flex-col gap-3 rounded-xl border border-border/60 p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{lote.credenciadoNome}</p>
                <p className="text-xs text-muted-foreground">
                  {lote.dreCodigo} · {rotuloCurtoDaCompetencia(lote.competencia)} ·{' '}
                  {lote.diasApontados} dia(s) · aguardando há {lote.diasAguardando} dia(s)
                </p>
              </div>
              <Button
                size="sm"
                className="w-full sm:w-auto"
                onClick={() => navegar('/sme/apontamentos')}
              >
                Revisar
                <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
